import { styled, keyframes } from 'styled-components';
import { CloseIcon } from '@icons';
import { ToastVariant } from './Toast.props';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

export const ToastContainer = styled.div`
  position: fixed;
  top: 100px;
  right: ${({ theme }) => theme.sizes.sm};
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizes['2xs']};
  pointer-events: none;
  max-width: calc(100vw - ${({ theme }) => theme.sizes.sm} * 2);
`;

const getBackgroundColor = (variant: ToastVariant) => {
  switch (variant) {
    case 'success':
      return '#00A63E'; // green600
    case 'error':
      return '#C10007'; // red700
    case 'warning':
      return '#BE7710'; // secondary600
    case 'info':
      return '#0084D1'; // blue600
    default:
      return '#00A63E';
  }
};

export const ToastWrapper = styled.div<{ $isExiting: boolean; $variant: ToastVariant }>`
  background-color: ${({ $variant }) => getBackgroundColor($variant)};
  border-radius: 8px;
  padding: ${({ theme }) => theme.sizes.sm};
  min-width: 280px;
  max-width: 400px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizes.sm};
  pointer-events: auto;
  animation: ${({ $isExiting }) => ($isExiting ? slideOut : slideIn)} 0.3s ease-out;
  position: relative;
`;

export const ToastIconSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

export const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ToastTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral};
  line-height: 1.2;
`;

export const ToastMessage = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.neutral};
  line-height: 1.4;
`;

export const ToastCloseDivider = styled.span`
  flex-shrink: 0;
  align-self: stretch;
  width: 1px;
  min-height: 36px;
  background-color: rgba(255, 255, 255, 0.45);
`;

export const ToastCloseIcon = styled(CloseIcon)`
  color: ${({ theme }) => theme.colors.neutral};
  width: 16px;
  height: 16px;
`;

export const ToastCloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.neutral};
  opacity: 0.9;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.neutral};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
